import { type GapTemplate } from './types';

export const GAP_TEMPLATES: GapTemplate[] = [
  {
    gapKey: 'hba1c_trend',
    domain: 'CLINICAL',
    gapLabel: 'HbA1c Trend',
    defaultSeverity: 'HIGH',
    questions: [
      {
        question: 'When was your last HbA1c test, and what was the result?',
        rationale: 'HbA1c trend directly impacts glycemic management targets.',
      },
      {
        question: 'Have you noticed changes in your energy levels or thirst recently?',
        rationale: 'Symptom patterns can indicate uncontrolled glucose before lab confirmation.',
      },
    ],
  },
  {
    gapKey: 'medication_adherence',
    domain: 'CLINICAL',
    gapLabel: 'Medication Adherence',
    defaultSeverity: 'HIGH',
    questions: [
      {
        question: 'Are you taking all prescribed medications consistently, and have there been any missed doses recently?',
        rationale: 'Medication gaps are a leading cause of CGM outlier readings.',
      },
      {
        question: 'Have you experienced any side effects that make you hesitant to take your medications?',
        rationale: 'Side-effect barriers are a common undisclosed reason for poor adherence.',
      },
    ],
  },
  {
    gapKey: 'comorbidity_management',
    domain: 'CLINICAL',
    gapLabel: 'Comorbidity Management',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'Are you managing any other health conditions alongside your diabetes, such as hypertension or thyroid issues?',
        rationale: 'Comorbidities can significantly affect glucose levels and dietary requirements.',
      },
    ],
  },
  {
    gapKey: 'sleep_quality',
    domain: 'CLINICAL',
    gapLabel: 'Sleep Quality',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'How many hours of sleep do you typically get, and do you wake up feeling rested?',
        rationale: 'Poor sleep is strongly linked to insulin resistance and next-day glucose spikes.',
      },
      {
        question: 'Have you been evaluated for sleep apnea or other sleep disorders?',
        rationale: 'Undiagnosed sleep apnea is associated with poor glycemic control.',
      },
    ],
  },
  {
    gapKey: 'exercise_routine',
    domain: 'BEHAVIORAL',
    gapLabel: 'Exercise Routine',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'How frequently do you exercise each week, and what types of activity do you prefer?',
        rationale: 'Exercise frequency and type directly impact insulin sensitivity and glucose levels.',
      },
      {
        question: 'Do you notice changes in your CGM readings after physical activity?',
        rationale: 'Understanding post-exercise glucose patterns helps refine nutrition timing.',
      },
    ],
  },
  {
    gapKey: 'meal_timing_patterns',
    domain: 'BEHAVIORAL',
    gapLabel: 'Meal Timing Patterns',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'Do you follow a regular eating schedule, or do meal times vary significantly day to day?',
        rationale: 'Irregular meal timing is associated with greater glycemic variability.',
      },
      {
        question: 'Do you tend to skip meals, especially breakfast?',
        rationale: 'Meal skipping can cause compensatory overeating and glucose swings.',
      },
    ],
  },
  {
    gapKey: 'goal_progress_review',
    domain: 'BEHAVIORAL',
    gapLabel: 'Goal Progress Review',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'How are you progressing toward your current nutrition and glucose goals, and what barriers are making them difficult right now?',
        rationale: 'Goal progress and barriers help determine whether an active care plan is still aligned with the patient’s day-to-day reality.',
      },
      {
        question: 'Have any of your priorities changed since your last care-plan update, such as weight, time in range, meal planning, or medication routines?',
        rationale: 'Shifting priorities can create care gaps even when existing goals appear active on paper.',
      },
    ],
  },
  {
    gapKey: 'stress_management',
    domain: 'BEHAVIORAL',
    gapLabel: 'Stress Management',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'On a scale of 1–10, how would you rate your current stress levels, and what are the main sources?',
        rationale: 'Chronic stress elevates cortisol, which raises blood glucose.',
      },
      {
        question: 'Do you have strategies to manage stress, such as mindfulness, exercise, or relaxation techniques?',
        rationale: 'Identifying coping mechanisms helps tailor holistic recommendations.',
      },
    ],
  },
  {
    gapKey: 'alcohol_consumption',
    domain: 'BEHAVIORAL',
    gapLabel: 'Alcohol Consumption',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'How often do you consume alcoholic beverages, and in what quantities?',
        rationale: 'Alcohol can cause delayed hypoglycemia and interfere with CGM readings.',
      },
    ],
  },
  {
    gapKey: 'cultural_food_preferences',
    domain: 'CULTURAL',
    gapLabel: 'Cultural Food Preferences',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'Are there traditional or cultural foods that are important to include in your diet?',
        rationale: 'Culturally relevant food choices improve protocol adherence and patient satisfaction.',
      },
      {
        question: 'Are there any foods you avoid for religious, cultural, or personal reasons?',
        rationale: 'Dietary restrictions must be respected to maintain trust and compliance.',
      },
    ],
  },
  {
    gapKey: 'fasting_religious_practices',
    domain: 'CULTURAL',
    gapLabel: 'Religious / Cultural Fasting',
    defaultSeverity: 'HIGH',
    questions: [
      {
        question: 'Do you observe any religious or cultural fasting periods (e.g., Ramadan, Lent, Yom Kippur)?',
        rationale: 'Fasting periods require protocol adjustment to prevent hypoglycemia.',
      },
      {
        question: 'If you do fast, how do you usually adjust meals, medications, or monitoring during that time?',
        rationale: 'Operational details are needed to make fasting safe with diabetes management.',
      },
    ],
  },
  {
    gapKey: 'health_beliefs',
    domain: 'CULTURAL',
    gapLabel: 'Health Beliefs',
    defaultSeverity: 'LOW',
    questions: [
      {
        question: 'Are there any cultural or family health beliefs that influence how you manage diabetes or nutrition?',
        rationale: 'Belief systems often shape adherence, trust, and treatment preferences.',
      },
    ],
  },
  {
    gapKey: 'family_history_diabetes',
    domain: 'FAMILY',
    gapLabel: 'Family History Of Diabetes',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'Does anyone in your immediate family have diabetes or prediabetes?',
        rationale: 'Family history affects risk stratification and patient understanding of the condition.',
      },
    ],
  },
  {
    gapKey: 'family_support_system',
    domain: 'FAMILY',
    gapLabel: 'Family Support System',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'Who helps support your meal planning, shopping, or diabetes management at home?',
        rationale: 'Support systems can improve adherence or reveal hidden barriers.',
      },
      {
        question: 'Do the people you live with follow similar eating patterns, or is it hard to manage your plan around them?',
        rationale: 'Household eating dynamics often drive success or failure of nutrition plans.',
      },
    ],
  },
  {
    gapKey: 'family_cardiovascular_history',
    domain: 'FAMILY',
    gapLabel: 'Family Cardiovascular History',
    defaultSeverity: 'LOW',
    questions: [
      {
        question: 'Is there a family history of heart disease, high blood pressure, or stroke?',
        rationale: 'Cardiometabolic family history informs long-term nutrition and risk goals.',
      },
    ],
  },
  {
    gapKey: 'food_security',
    domain: 'SDOH',
    gapLabel: 'Food Security',
    defaultSeverity: 'HIGH',
    questions: [
      {
        question: 'In the past month, have you worried about running out of food or not being able to afford healthy meals?',
        rationale: 'Food insecurity is a primary barrier to diabetes nutrition management.',
      },
    ],
  },
  {
    gapKey: 'work_schedule_constraints',
    domain: 'SDOH',
    gapLabel: 'Work Schedule Constraints',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'Does your work schedule make it difficult to eat regularly, monitor glucose, or prepare meals?',
        rationale: 'Shift work and irregular schedules can disrupt medication and meal timing.',
      },
    ],
  },
  {
    gapKey: 'transportation_barriers',
    domain: 'SDOH',
    gapLabel: 'Transportation Barriers',
    defaultSeverity: 'LOW',
    questions: [
      {
        question: 'Do you have reliable transportation for grocery shopping, appointments, and pharmacy pickups?',
        rationale: 'Transportation issues can block access to care and healthy food.',
      },
    ],
  },
  {
    gapKey: 'financial_barriers',
    domain: 'SDOH',
    gapLabel: 'Financial Barriers',
    defaultSeverity: 'MEDIUM',
    questions: [
      {
        question: 'Are the costs of medications, CGM supplies, or recommended foods creating any challenges right now?',
        rationale: 'Financial burden is a common reason for care plan non-adherence.',
      },
    ],
  },
  {
    gapKey: 'social_isolation',
    domain: 'SDOH',
    gapLabel: 'Social Isolation',
    defaultSeverity: 'LOW',
    questions: [
      {
        question: 'Do you feel socially supported, or do you often manage meals and health concerns on your own?',
        rationale: 'Isolation can worsen adherence, motivation, and mental health.',
      },
    ],
  },
];